import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16" data-aos="fade-up">
          <p className="text-primary text-sm font-semibold tracking-wider uppercase mb-3">
            Contact Us
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-secondary mb-4 font-display">
            문의하기
          </h2>
          <p className="text-gray-600 text-base max-w-2xl mx-auto">
            궁금하신 사항이 있으시면 언제든지 연락주세요. 전문 상담원이 친절하게 답변해드립니다.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div data-aos="fade-right">
            <div className="bg-gradient-to-br from-primary to-accent rounded-2xl p-8 md:p-12 text-white h-full">
              <h3 className="text-2xl font-bold mb-8 font-display">연락처 정보</h3>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">주소</div>
                    <div className="text-sm text-white/90">
                      경기도 안산시 단원구 광덕4로250 404호<br />
                      (시티프라자)
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">전화</div>
                    <div className="text-sm text-white/90">1666-8482</div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                    <i className="ri-printer-line text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">팩스</div>
                    <div className="text-sm text-white/90">0504-363-1573</div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">이메일</div>
                    <div className="text-sm text-white/90">hansung_2020@naver.com</div>
                  </div>
                </div>
              </div>

              <div>
                <div className="font-semibold mb-4">소셜 미디어</div>
                <div className="flex gap-3">
                  <a
                    href="http://pf.kakao.com/_xgHVCxj"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition-colors cursor-pointer"
                  >
                    <img src="https://www.whizcompany.com/images/common/sns-icn1.png" alt="카카오톡" className="w-5 h-5 object-contain" />
                  </a>
                  <a
                    href="https://www.instagram.com/whizcompany?igsh=dGk1eDJ0MTcwbmE4&utm_source=qr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition-colors cursor-pointer"
                  >
                    <img src="https://www.whizcompany.com/images/common/sns-icn2.png" alt="인스타그램" className="w-5 h-5 object-contain" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div data-aos="fade-left">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-secondary mb-2">
                  이름 *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-sm"
                  placeholder="이름을 입력하세요"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-secondary mb-2">
                    이메일 *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-secondary mb-2">
                    연락처 *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-sm"
                    placeholder="010-0000-0000"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-secondary mb-2">
                  문의 유형 *
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">선택하세요</option>
                  <option value="분양대행">부동산 분양대행</option>
                  <option value="투자자문">부동산 투자자문</option>
                  <option value="개발사업">부동산 개발사업</option>
                  <option value="자산관리">부동산 자산관리</option>
                  <option value="기타">기타 문의</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-secondary mb-2">
                  문의 내용 *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none text-sm"
                  placeholder="문의하실 내용을 입력하세요 (최대 500자)"
                ></textarea>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-accent text-white py-4 rounded-lg font-semibold transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-sm"
              >
                {isSubmitting ? '전송 중...' : '문의하기'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  문의가 성공적으로 전송되었습니다. 빠른 시일 내에 답변드리겠습니다.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  문의 전송에 실패했습니다. 다시 시도해주세요.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
